// Footnotes module
// Converts {{tooltip-title: key}} and {{tooltip-content: key}} ... {{/tooltip-content}} to interactive tooltips

// ─── Singleton popover ────────────────────────────────────────────────────────

let _popover = null;
let _footnotesMap = null;
let _activeRef = null;
let _hideTimeout = null;
let _showTimeout = null;

export function initTooltipSingleton(footnotesMap) {
  if (_popover) return; // already initialized

  _footnotesMap = footnotesMap;

  _popover = document.createElement('div');
  _popover.className = 'tooltip-popover';
  _popover.setAttribute('role', 'tooltip');
  document.body.appendChild(_popover);

  // Keep popover open while hovering over it
  if (window.matchMedia('(hover: hover)').matches) {
    _popover.addEventListener('mouseenter', () => {
      if (_hideTimeout) { clearTimeout(_hideTimeout); _hideTimeout = null; }
    });
    _popover.addEventListener('mouseleave', () => { _scheduleHide(); });
  }

  // Close on outside click
  document.addEventListener('click', (e) => {
    if (_popover && !_popover.contains(e.target) &&
        !e.target.closest('.tooltip-ref')) {
      _hidePopover();
    }
  });

  // Close on Escape
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && _popover && _popover.classList.contains('is-active')) {
      _hidePopover();
      if (_activeRef) _activeRef.focus();
    }
  });

  // Reposition on scroll / resize
  let _resizeFrame = null;
  function _onResize() {
    if (_resizeFrame) cancelAnimationFrame(_resizeFrame);
    _resizeFrame = requestAnimationFrame(() => {
      if (_activeRef && _popover.classList.contains('is-active')) {
        _positionPopover(_activeRef);
      }
    });
  }
  window.addEventListener('scroll', _onResize, { passive: true });
  window.addEventListener('resize', _onResize);
}

function _showPopover(ref) {
  if (_hideTimeout) { clearTimeout(_hideTimeout); _hideTimeout = null; }
  if (_activeRef && _activeRef !== ref) _hidePopover(true);

  _activeRef = ref;

  const key = ref.getAttribute('data-tooltip-key');
  if (!key || !_footnotesMap || !_footnotesMap.has(key)) return;

  const content = _footnotesMap.get(key);
  if (!content) return;

  _popover.innerHTML = content; // intentional: content is authored in Ghost CMS
  _positionPopover(ref);
  _popover.classList.add('is-active');
  ref.classList.add('is-active');
}

function _hidePopover(immediate = false) {
  if (immediate) {
    _doHide();
    return;
  }
  if (_hideTimeout) return;
  _hideTimeout = setTimeout(_doHide, 100);
}

function _doHide() {
  if (_popover) _popover.classList.remove('is-active');
  if (_activeRef) { _activeRef.classList.remove('is-active'); _activeRef = null; }
  _hideTimeout = null;
}

function _scheduleHide() {
  if (_hideTimeout) return;
  _hideTimeout = setTimeout(_doHide, 100);
}

function _positionPopover(ref) {
  const rect = ref.getBoundingClientRect();
  const scrollY = window.scrollY;
  const scrollX = window.scrollX;
  const popoverWidth = 320;
  const viewportWidth = window.innerWidth;

  let top = rect.bottom + scrollY + 8;
  let left = rect.left + scrollX;

  if (left + popoverWidth > viewportWidth) left = viewportWidth - popoverWidth - 20;
  if (left < 20) left = 20;

  // Measure height to decide above/below
  _popover.style.visibility = 'hidden';
  _popover.style.display = 'block';
  const popoverHeight = _popover.offsetHeight || 200;
  _popover.style.display = '';
  _popover.style.visibility = '';

  if (rect.bottom + popoverHeight > window.innerHeight) {
    top = rect.top + scrollY - popoverHeight - 8;
    _popover.classList.add('is-above');
  } else {
    _popover.classList.remove('is-above');
  }

  _popover.style.top = `${top}px`;
  _popover.style.left = `${left}px`;
}

/**
 * Attach event listeners to newly created .tooltip-ref elements.
 * Safe to call multiple times — only processes refs that don't already have the listener.
 */
export function registerTooltipRefs(refs) {
  if (!_popover) return; // singleton not initialized yet

  refs.forEach(ref => {
    if (ref.dataset.tooltipBound) return;
    ref.dataset.tooltipBound = '1';

    ref.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (_activeRef === ref && _popover.classList.contains('is-active')) {
        _hidePopover();
      } else {
        if (_showTimeout) { clearTimeout(_showTimeout); _showTimeout = null; }
        _showPopover(ref);
      }
    });

    if (window.matchMedia('(hover: hover)').matches) {
      ref.addEventListener('mouseenter', () => {
        if (_showTimeout) clearTimeout(_showTimeout);
        _showTimeout = setTimeout(() => { _showPopover(ref); }, 300);
      });
      ref.addEventListener('mouseleave', () => {
        if (_showTimeout) { clearTimeout(_showTimeout); _showTimeout = null; }
        _scheduleHide();
      });
    }

    ref.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        e.stopPropagation();
        if (_activeRef === ref && _popover.classList.contains('is-active')) {
          _hidePopover();
        } else {
          if (_showTimeout) { clearTimeout(_showTimeout); _showTimeout = null; }
          _showPopover(ref);
        }
      } else if (e.key === 'Escape' && _activeRef === ref) {
        _hidePopover();
        ref.focus();
      }
    });
  });
}

// ─── Definition processing ────────────────────────────────────────────────────

export function initFootnotes() {
  const contentElement = document.querySelector('.gh-content');
  if (!contentElement) return { footnotesMap: new Map() };

  const footnotesMap = new Map();
  const allElements = Array.from(contentElement.children);
  const startRegex = /\{\{tooltip-content:\s*([^}]+)\}\}/;
  const endRegex = /\{\{\/tooltip-content\}\}/;

  for (let i = 0; i < allElements.length; i++) {
    const element = allElements[i];
    const text = element.textContent || '';
    const startMatch = text.match(startRegex);
    if (!startMatch) continue;

    const tooltipKey = startMatch[1].trim();
    if (footnotesMap.has(tooltipKey)) continue;

    let endIndex = -1;
    let endElement = null;
    for (let j = i; j < allElements.length; j++) {
      if ((allElements[j].textContent || '').match(endRegex)) {
        endIndex = j; endElement = allElements[j]; break;
      }
    }

    if (endIndex === -1) {
      console.warn(`[Footnotes] Tooltip definition for "${tooltipKey}" has no closing {{/tooltip-content}}.`);
      continue;
    }

    const contentParts = [];
    const elementsToRemove = [];

    // Start element
    const startHTML = element.innerHTML || '';
    const escapedKey = tooltipKey.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const startHTMLMatch = startHTML.match(new RegExp(`\\{\\{tooltip-content:\\s*${escapedKey}\\}\\}\\s*(.+)`, 's'));
    if (startHTMLMatch?.[1]?.trim()) contentParts.push(startHTMLMatch[1].trim());

    if (element.textContent.trim() === startMatch[0].trim()) {
      elementsToRemove.push(element);
    } else if (startHTMLMatch) {
      const updated = startHTML.replace(startHTMLMatch[0], '').trim();
      updated ? (element.innerHTML = updated) : elementsToRemove.push(element);
    }

    // Middle elements
    for (let k = i + 1; k < endIndex; k++) {
      contentParts.push(allElements[k].outerHTML);
      elementsToRemove.push(allElements[k]);
    }

    // End element
    const endHTML = endElement.innerHTML || '';
    const endHTMLMatch = endHTML.match(/(.+?)\s*\{\{\/tooltip-content\}\}/s);
    if (endHTMLMatch?.[1]?.trim()) contentParts.push(endHTMLMatch[1].trim());

    if (endElement.textContent.trim() === '{{/tooltip-content}}') {
      elementsToRemove.push(endElement);
    } else if (endHTMLMatch) {
      const updated = endHTML.replace(endHTMLMatch[0], '').trim();
      updated ? (endElement.innerHTML = updated) : elementsToRemove.push(endElement);
    }

    footnotesMap.set(tooltipKey, contentParts.join(''));
    elementsToRemove.forEach(el => el.parentNode?.removeChild(el));
    i = endIndex;
  }

  // Process top-level tooltip references
  processTooltipReferences(contentElement, footnotesMap);

  return { footnotesMap };
}

// ─── Reference processing (used for top-level and nested containers) ──────────

export function processTooltipReferences(container, footnotesMap) {
  if (!container || !footnotesMap) return;

  const referenceRegex = /\{\{tooltip-title:\s*([^}]+)\}\}/g;
  const selectors = 'p, li, figcaption, .kg-callout-text, h1, h2, h3, h4, h5, h6';

  container.querySelectorAll(selectors).forEach(element => {
    // Skip already-processed elements
    if (element.querySelector('.tooltip-ref')) return;

    const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT, null, false);
    const textNodes = [];
    let node;
    while ((node = walker.nextNode())) textNodes.push(node);

    textNodes.forEach(textNode => {
      const text = textNode.textContent;
      const matches = [...text.matchAll(referenceRegex)];
      if (!matches.length) return;

      const fragment = document.createDocumentFragment();
      let lastIndex = 0;

      matches.forEach(match => {
        const key = match[1].trim();
        if (match.index > lastIndex) {
          fragment.appendChild(document.createTextNode(text.slice(lastIndex, match.index)));
        }

        if (!footnotesMap.has(key)) {
          fragment.appendChild(document.createTextNode(match[0]));
          lastIndex = match.index + match[0].length;
          return;
        }

        const tooltipId = `tooltip-${key.replace(/\s+/g, '-').toLowerCase().replace(/[^a-z0-9-]/g, '')}`;
        const span = document.createElement('span');
        span.className = 'tooltip-ref';
        span.setAttribute('data-tooltip-id', tooltipId);
        span.setAttribute('data-tooltip-key', key);
        span.setAttribute('tabindex', '0');
        span.setAttribute('role', 'button');
        span.setAttribute('aria-label', `Tooltip: ${key}`);

        const titleSpan = document.createElement('span');
        titleSpan.className = 'tooltip-title';
        titleSpan.textContent = key;
        span.appendChild(titleSpan);

        fragment.appendChild(span);
        lastIndex = match.index + match[0].length;
      });

      if (lastIndex < text.length) {
        fragment.appendChild(document.createTextNode(text.slice(lastIndex)));
      }

      textNode.parentNode?.replaceChild(fragment, textNode);
    });
  });

  // Register newly created refs with the singleton
  registerTooltipRefs(Array.from(container.querySelectorAll('.tooltip-ref:not([data-tooltip-bound])')));

  // Validation: warn about refs with no definition
  const allText = container.textContent || '';
  const referenceRegexFresh = /\{\{tooltip-title:\s*([^}]+)\}\}/g;
  for (const match of allText.matchAll(referenceRegexFresh)) {
    const key = match[1].trim();
    if (!footnotesMap.has(key)) {
      console.warn(`[Footnotes] Reference "{{tooltip-title: ${key}}}" has no matching definition.`);
    }
  }
}
